import { type ReactNode } from 'react';
import styled, { css } from 'styled-components';

import BackChevronIconSvg from '../../../assets/icons/icon-back-chevron.svg?react';
import { BaseButton, rubikTypographyStyles } from '../../../shared/ui';

export type MyPageSettingsListItem = {
  icon: ReactNode;
  key: string;
  label: string;
  onPress?: () => void;
  showChevron?: boolean;
  tone?: 'default' | 'danger';
  trailing?: ReactNode;
};

type MyPageSettingsListProps = {
  items: ReadonlyArray<MyPageSettingsListItem>;
  title?: string;
};

const Section = styled.section.attrs({
  'data-ui': 'my-page-settings-section',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(14)};
`;

const Title = styled.h2`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(19)};
`;

const Card = styled.div.attrs({
  'data-ui': 'my-page-settings-card',
})`
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  overflow: hidden;
`;

const RowDivider = styled.div`
  height: ${({ theme }) => theme.scale.px(1)};
  background: ${({ theme }) => theme.colors.border.divider};
`;

const rowStyles = css`
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  background: transparent;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  column-gap: ${({ theme }) => theme.scale.px(18)};
  text-align: left;
`;

const ActionRow = styled(BaseButton).attrs({
  'data-ui': 'my-page-settings-row',
})`
  ${rowStyles};
  min-height: ${({ theme }) => theme.scale.px(48)};
  padding: ${({ theme }) => theme.scale.px(15)}
    ${({ theme }) => theme.scale.px(22)};
`;

const StaticRow = styled.div.attrs({
  'data-ui': 'my-page-settings-row',
})`
  ${rowStyles};
  min-height: ${({ theme }) => theme.scale.px(48)};
  padding: ${({ theme }) => theme.scale.px(15)}
    ${({ theme }) => theme.scale.px(22)};
`;

const IconSlot = styled.span`
  width: ${({ theme }) => theme.scale.px(18)};
  height: ${({ theme }) => theme.scale.px(18)};
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.text.primary};

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const Label = styled.span<{ $tone: 'default' | 'danger' }>`
  min-width: 0;
  color: ${({ $tone, theme }) =>
    $tone === 'danger'
      ? theme.colors.support.danger
      : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(18)};
`;

const TrailingSlot = styled.span`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
`;

const ChevronIcon = styled(BackChevronIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(6)};
  height: ${({ theme }) => theme.scale.px(11)};
  color: rgba(0, 0, 0, 0.6);
  transform: rotate(180deg);

  & path {
    stroke: currentColor;
  }
`;

function MyPageSettingsListRow({
  icon,
  label,
  onPress,
  showChevron = true,
  tone = 'default',
  trailing,
}: Omit<MyPageSettingsListItem, 'key'>) {
  const trailingContent =
    trailing ?? (showChevron ? <ChevronIcon /> : <span aria-hidden />);

  const body = (
    <>
      <IconSlot>{icon}</IconSlot>
      <Label $tone={tone}>{label}</Label>
      <TrailingSlot>{trailingContent}</TrailingSlot>
    </>
  );

  if (onPress) {
    return (
      <ActionRow type="button" onClick={onPress}>
        {body}
      </ActionRow>
    );
  }

  return <StaticRow>{body}</StaticRow>;
}

export function MyPageSettingsList({ items, title }: MyPageSettingsListProps) {
  return (
    <Section>
      {title ? <Title>{title}</Title> : null}

      <Card>
        {items.map((item, index) => (
          <div key={item.key}>
            <MyPageSettingsListRow {...item} />
            {index < items.length - 1 ? <RowDivider /> : null}
          </div>
        ))}
      </Card>
    </Section>
  );
}
